import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  getBuybackAlert,
  ignorePendingConfirmAlert,
  subscribeBuybackAlert,
} from '@/api/common/orderAlert'
import { getToken } from '@/utils/auth'
import type { BuybackOrderAlert } from '@/types/orderAlert'

const EMPTY_ALERT: BuybackOrderAlert = {
  serverTime: '',
  thresholdHours: 2,
  thresholdTime: '',
  hasOverdue: false,
  overdueCount: 0,
  records: [],
}

const useOrderAlertStore = defineStore('orderAlert', () => {
  const snapshot = ref<BuybackOrderAlert>({ ...EMPTY_ALERT })
  const loading = ref(false)
  const streaming = ref(false)
  const streamError = ref('')
  const removingOrderIds = ref<string[]>([])

  let closeStream: null | (() => void) = null

  const hasOverdue = computed(() => snapshot.value.hasOverdue)
  const overdueCount = computed(() => snapshot.value.overdueCount)
  const records = computed(() => snapshot.value.records)

  function stopStream() {
    if (closeStream) {
      closeStream()
      closeStream = null
    }
    streaming.value = false
  }

  function reset() {
    stopStream()
    snapshot.value = { ...EMPTY_ALERT }
    streamError.value = ''
    removingOrderIds.value = []
  }

  function updateSnapshot(data: BuybackOrderAlert) {
    snapshot.value = {
      ...data,
      hasOverdue: data.overdueCount > 0,
    }
  }

  function removeOrderFromSnapshot(orderId: string) {
    const targetId = String(orderId)
    const exists = snapshot.value.records.some((item) => item.orderId === targetId)

    if (!exists) {
      return
    }

    const nextRecords = snapshot.value.records.filter(
      (item) => item.orderId !== targetId
    )
    const nextOverdueCount = Math.max(0, snapshot.value.overdueCount - 1)

    snapshot.value = {
      ...snapshot.value,
      records: nextRecords,
      overdueCount: nextOverdueCount,
      hasOverdue: nextOverdueCount > 0,
    }
  }

  async function loadSnapshot() {
    const token = getToken()

    if (!token) {
      reset()
      return
    }

    loading.value = true
    try {
      const data = await getBuybackAlert(token)
      updateSnapshot(data)
      streamError.value = ''
    } finally {
      loading.value = false
    }
  }

  function startStream() {
    const token = getToken()

    if (!token) {
      return
    }

    stopStream()
    streaming.value = true
    streamError.value = ''

    closeStream = subscribeBuybackAlert({
      token,
      onAlert(data) {
        updateSnapshot(data)
        streamError.value = ''
      },
      onError(error) {
        streamError.value =
          error instanceof Error ? error.message : '预警流连接异常'
      },
    })
  }

  async function dismissReminder(orderId: number | string) {
    const normalizedOrderId = String(orderId ?? '').trim()

    if (
      !normalizedOrderId ||
      removingOrderIds.value.includes(normalizedOrderId)
    ) {
      return false
    }

    removingOrderIds.value = [...removingOrderIds.value, normalizedOrderId]

    try {
      await ignorePendingConfirmAlert(normalizedOrderId)
      removeOrderFromSnapshot(normalizedOrderId)
      return true
    } finally {
      removingOrderIds.value = removingOrderIds.value.filter(
        (item) => item !== normalizedOrderId
      )
    }
  }

  async function init() {
    await loadSnapshot()
    startStream()
  }

  return {
    snapshot,
    loading,
    streaming,
    streamError,
    removingOrderIds,
    hasOverdue,
    overdueCount,
    records,
    loadSnapshot,
    startStream,
    stopStream,
    dismissReminder,
    init,
    reset,
  }
})

export default useOrderAlertStore
